/**
 * Mission Store Getters
 *
 * Platform-independent getter functions that wrap ActionTreeUtils.
 * These provide convenient accessors for common mission queries.
 *
 * @packageDocumentation
 */

import { FrameType_Enum } from "@skydio/pbtypes/pbtypes/vehicle/skills/waypoints";

import {
  findActions,
  findFirstAction,
  getRootSequence,
  getWaypointActions,
  isActionType,
  isGPSInitHover,
  isWaypoint,
} from "../actionTree";

import type { Action, Mission } from "@skydio/pbtypes/pbtypes/vehicle/skills/mission";
import type { GPSInitWaypoint, Waypoint } from "../actionTree";

/**
 * Mission type classification
 */
export type MissionType = "waypoints" | "mapScan" | "mixed";

/**
 * Safe waypoint frames that can be edited in the UI
 *
 * @remarks
 * GPS and SITE frames can be freely edited in the UI as they represent
 * geographic coordinates that can be adjusted spatially.
 */
export const SAFE_WAYPOINT_FRAMES: Set<FrameType_Enum> = new Set([
  FrameType_Enum.GPS,
  FrameType_Enum.SITE,
]);

/**
 * Unsafe waypoint frames that shouldn't be edited in the UI
 *
 * @remarks
 * These frames represent vehicle-relative or map-specific coordinates that
 * shouldn't be moved spatially. Examples:
 * - NAV: Vehicle-relative from autonomy engine start
 * - NAV_CAM_POSE_GRAPH: Camera pose graph coordinates
 * - NAV_TAKEOFF: Takeoff-relative coordinates
 * - WORLD: GPS-aligned linearized coordinates
 *
 * Any frame not in SAFE_WAYPOINT_FRAMES is considered unsafe to edit.
 */
export const UNSAFE_WAYPOINT_FRAMES: Set<FrameType_Enum> = new Set([
  FrameType_Enum.NAV,
  FrameType_Enum.NAV_CAM_POSE_GRAPH,
  FrameType_Enum.NAV_TAKEOFF,
  FrameType_Enum.WORLD,
  FrameType_Enum.NAV_HEIGHT_ABOVE_GROUND,
  FrameType_Enum.WORLD_PILOT_RELATIVE,
  FrameType_Enum.WORLD_TAKEOFF,
  FrameType_Enum.WORLD_DOCK_RELATIVE,
  FrameType_Enum.NAV_DOCK_RELATIVE,
]);

/**
 * Get all waypoints in a mission (excluding GPS init waypoint)
 *
 * @remarks
 * This function filters out the GPS initialization hover waypoint and returns
 * only the actual mission waypoints.
 *
 * @param mission - The mission to get waypoints from
 * @returns Array of waypoint actions
 */
export function getWaypoints(mission: Mission): Waypoint[] {
  return findActions(mission, action => isWaypoint(action) && !isGPSInitHover(action)).map(
    result => result.action as Waypoint
  );
}

/**
 * Get first non-GPS-init waypoint
 *
 * @remarks
 * This is useful for determining the coordinate frame and other properties
 * of the mission from its first waypoint.
 *
 * @param mission - The mission to search
 * @returns First waypoint or null if none found
 */
export function getFirstWaypoint(mission: Mission): Waypoint | null {
  const result = findFirstAction(mission, action => isWaypoint(action) && !isGPSInitHover(action));
  return result ? (result.action as Waypoint) : null;
}

/**
 * Count waypoints in mission (excluding GPS init)
 *
 * @param mission - The mission to count waypoints in
 * @returns Number of waypoints
 */
export function getWaypointCount(mission: Mission): number {
  return getWaypoints(mission).length;
}

/**
 * Check if mission has surface scan skill
 *
 * @param mission - The mission to check
 * @returns True if mission contains a surface scan action
 */
export function hasSurfaceScan(mission: Mission): boolean {
  return findActions(mission, action => isActionType(action, "surfaceScanSkill"), 1).length > 0;
}

/**
 * Determine mission type based on actions
 *
 * @remarks
 * Mission types:
 * - waypoints: Contains only waypoint actions
 * - mapScan: Contains surface scan skill
 * - mixed: Contains both waypoints and surface scan
 *
 * @param mission - The mission to classify
 * @returns Mission type classification
 */
export function getMissionType(mission: Mission): MissionType {
  const hasWaypoints = getWaypointCount(mission) > 0;
  const hasScan = hasSurfaceScan(mission);

  if (hasScan && hasWaypoints) {
    return "mixed";
  } else if (hasScan) {
    return "mapScan";
  } else {
    return "waypoints";
  }
}

/**
 * Get root sequence actions
 *
 * @remarks
 * The root sequence is the top-level container for all mission actions.
 * This function provides direct access to its actions array.
 *
 * @param mission - The mission to get root actions from
 * @returns Array of actions in the root sequence
 */
export function getRootSequenceActions(mission: Mission): Action[] {
  return getRootSequence(mission).actions;
}

/**
 * Get waypoints in unsafe frames (NAV, VPS, NCPG)
 *
 * @remarks
 * Waypoints in these frames shouldn't be moved spatially in the UI as they
 * reference specific vehicle-relative or map-specific coordinates.
 *
 * @param mission - The mission to search
 * @returns Array of waypoints in unsafe frames
 */
export function getUnsafeWaypoints(mission: Mission): Waypoint[] {
  return findActions(mission, action => {
    if (!isWaypoint(action)) return false;
    const frame = getWaypointActions(action).position.args.gotoWaypoint?.waypoint?.xy?.frame;
    return frame != null && UNSAFE_WAYPOINT_FRAMES.has(frame);
  }).map(result => result.action as Waypoint);
}

/**
 * Check if mission has GPS waypoints
 *
 * @remarks
 * This checks if the mission contains any waypoints in GPS frame,
 * excluding the GPS init hover waypoint.
 *
 * @param mission - The mission to check
 * @returns True if mission has GPS waypoints
 */
export function hasGPSWaypoints(mission: Mission): boolean {
  const result = findFirstAction(mission, action => {
    if (!isWaypoint(action) || isGPSInitHover(action)) return false;
    const frame = getWaypointActions(action).position.args.gotoWaypoint?.waypoint?.xy?.frame;
    return frame === FrameType_Enum.GPS;
  });
  return result != null;
}

/**
 * Get GPS init hover waypoint if it exists
 *
 * @remarks
 * The GPS init hover is a special waypoint used to establish GPS lock
 * before starting the mission. It's automatically added for GPS missions.
 *
 * @param mission - The mission to search
 * @returns GPS init waypoint or null if not found
 */
export function getGPSInitHover(mission: Mission): GPSInitWaypoint | null {
  const result = findFirstAction(mission, action => isGPSInitHover(action));
  return result ? (result.action as GPSInitWaypoint) : null;
}

/**
 * Check if mission requires a map for rendering
 *
 * @remarks
 * Missions with GPS waypoints or map scans need a geographic map.
 * Other missions (like NAV or VPS) can render without a map background.
 *
 * @param mission - The mission to check
 * @returns True if mission should render with a map
 */
export function shouldRenderWorldMap(mission: Mission): boolean {
  return (
    findActions(
      mission,
      action =>
        (isWaypoint(action) &&
          SAFE_WAYPOINT_FRAMES.has(
            getWaypointActions(action).position.args.gotoWaypoint?.waypoint?.xy?.frame ??
              FrameType_Enum.GPS
          )) ||
        isActionType(action, "surfaceScanSkill"),
      1
    ).length > 0
  );
}

/**
 * Get all actions of a specific type
 *
 * @remarks
 * This is a generic helper for finding all actions of a specific type.
 * Use more specific getters when available for better type safety.
 *
 * @param mission - The mission to search
 * @param actionType - The action type to find
 * @returns Array of actions of the specified type
 */
export function getActionsByType<T extends string>(mission: Mission, actionType: T): Action[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return findActions(mission, action => isActionType(action, actionType as any)).map(
    result => result.action
  );
}

/**
 * Check if mission has any actions of a specific type
 *
 * @param mission - The mission to check
 * @param actionType - The action type to check for
 * @returns True if mission contains at least one action of the specified type
 */
export function hasActionType<T extends string>(mission: Mission, actionType: T): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return findActions(mission, action => isActionType(action, actionType as any), 1).length > 0;
}
